import { ANALYTICS, getTitleMap } from 'app/ROUTES_CONSTANTS'
import { getUserRoleString, useUser } from 'app/User'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useDashboardContext } from 'lib/context/DashboardContext'
import { useEffect, useState } from 'react'
import { useIsRouteActive } from 'lib/utils/isRouteActive'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import { sendFlushCommandToIframes } from 'lib/utils/iframeUtils'
import { useGetSurgeryQuery, useSetSurgeryStatus } from 'lib/apollo/hooks'
import { Surgery } from 'common/types'
import { enqueueSnackbar } from 'notistack'

export const useFloatingBarLogic = () => {
  const flags = useFlags()
  const navigate = useNavigate()
  const location = useLocation()
  const organizationSettings = useOrganizationSettings()
  const { showScrubTechnician } = useOrganizationSettings()
  const { surgeryId } = useParams<{ surgeryId: Surgery['_id'] | undefined }>()

  const [setSurgeryStatus] = useSetSurgeryStatus(surgeryId || '')

  const { data: surgeryData } = useGetSurgeryQuery(surgeryId, {
    disablePolling: true,
  })

  const isEditingSubmittedSurgery = surgeryId
    ? surgeryData?.getSurgery?.status?.isEditingSubmittedSurgery || false
    : false

  const { isActive } = useIsRouteActive()
  const { user, isNurseAdmin, isGuest, isRep } = useUser()
  const { setDarkMode, darkMode } = useDashboardContext()

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const userRoleString = user && getUserRoleString(user)
  const isSPD = user?.roles?.includes('MGAT_SPD')

  const getDynamicTitle = (location: ReturnType<typeof useLocation>) => {
    const titleMap = getTitleMap(organizationSettings)
    const rootPath = location.pathname.slice(1) as keyof typeof titleMap
    return titleMap[rootPath] ?? 'Platform'
  }

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handleLogout = () => {
    sendFlushCommandToIframes()
    // Add small delay before navigation
    setTimeout(() => {
      setIsMenuOpen(false)
      navigate('/logout')
    }, 200)
  }

  const handleNavigate = (route: string) => {
    setIsMenuOpen(false)
    navigate(`/${route}`)
  }

  const handleLogoBtnClick = () => {
    if (isGuest && !isRep) return
    handleNavigate(isSPD ? 'tray-management' : 'surgeries')
  }

  const handleThemeMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDarkMode(e.target.checked)
  }

  const handleExitAddendumMode = () => {
    if (!surgeryId) return
    setSurgeryStatus({
      variables: {
        surgeryId,
        status: 'SUBMITTED',
        isEditingSubmittedSurgery: false,
      },
    })
      .then(() => {
        navigate(`/surgeries/${surgeryId}`)
      })
      .catch((error) => {
        enqueueSnackbar('Error exiting addendum mode', {
          variant: 'error',
        })
      })
  }

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  return {
    user,
    isSPD,
    flags,
    darkMode,
    location,
    isMenuOpen,
    dynamicTitle: getDynamicTitle(location),
    isNurseAdmin,
    userRoleString,
    isDashboardActive: isActive(ANALYTICS),
    PhysiciansTitle: organizationSettings.physiciansTitle,
    isEditingSubmittedSurgery,
    isGuest,
    showScrubTechnician,
    handleExitAddendumMode,
    setDarkMode,
    handleLogout,
    handleNavigate,
    getDynamicTitle,
    handleThemeMode,
    handleMenuToggle,
    handleLogoBtnClick,
  }
}
